import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { PhotoIcon, TrashIcon, DocumentIcon } from '@heroicons/react/24/outline';
import { toast } from 'sonner';
import { fetchJsonOrThrow, getApiErrorMessage } from '../lib/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const getToken = () => {
  return localStorage.getItem('admin_token');
};

// Elegant Loading Animations Component
const LoadingAnimation = ({ type = 'upload' }) => {
  if (type === 'upload') {
    return (
      <div className="flex flex-col items-center justify-center space-y-4 py-8">
        {/* Expanding Bubbles Animation */}
        <div className="flex space-x-2">
          <div className="w-3 h-3 bg-blue-500 rounded-full animate-bubble-1"></div>
          <div className="w-3 h-3 bg-blue-400 rounded-full animate-bubble-2"></div>
          <div className="w-3 h-3 bg-blue-600 rounded-full animate-bubble-3"></div>
        </div>
        <p className="text-sm text-blue-600 font-medium animate-pulse">Uploading your file...</p>
      </div>
    );
  }

  if (type === 'processing') {
    return (
      <div className="flex flex-col items-center justify-center space-y-4 py-6">
        {/* Spinning Ring Loader */}
        <div className="relative w-12 h-12">
          <div className="absolute inset-0 border-4 border-blue-200 rounded-full"></div>
          <div className="absolute inset-0 border-4 border-blue-600 rounded-full border-t-transparent animate-spin-smooth"></div>
        </div>
        <p className="text-sm text-gray-600 animate-fade-in-out">Processing...</p>
      </div>
    );
  }

  return null;
};

// Skeleton Loader for Preview
const SkeletonLoader = () => {
  return (
    <div className="flex flex-col items-center space-y-3 p-4 border rounded-lg bg-gradient-to-br from-gray-50 to-gray-100 animate-fade-in">
      {/* Image Skeleton with Shimmer */}
      <div className="w-32 h-32 bg-gray-200 rounded-lg relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent animate-shimmer"></div>
      </div>
      
      {/* Text Skeleton Lines */}
      <div className="w-full space-y-2">
        <div className="h-3 bg-gray-200 rounded-full w-3/4 mx-auto relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent animate-shimmer-slow"></div>
        </div>
        <div className="h-3 bg-gray-200 rounded-full w-1/2 mx-auto relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent animate-shimmer-slow"></div>
        </div>
      </div>
    </div>
  );
};

const FileUpload = ({ 
  onFileUpload, 
  onFileRemove, 
  currentFileUrl = '', 
  accept = 'image/*,application/pdf',
  maxSize = 10 * 1024 * 1024, // 10MB default
  className = '' 
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const acceptedTypes = accept.split(',');
    const isTypeValid = acceptedTypes.some(type => {
      if (type === 'image/*') return file.type.startsWith('image/');
      if (type === 'application/pdf') return file.type === 'application/pdf';
      return file.type === type;
    });

    if (!isTypeValid) {
      toast.error('Please select a valid file type (PNG, JPG, PDF)');
      return;
    }

    // Validate file size
    if (file.size > maxSize) {
      toast.error(`File size should be less than ${maxSize / (1024 * 1024)}MB`);
      return;
    }

    setSelectedFile(file);
    setLoadingPreview(true);

    // Create preview for images with loading state
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setTimeout(() => {
          setPreview(e.target.result);
          setLoadingPreview(false);
        }, 500); // Smooth transition
      };
      reader.readAsDataURL(file);
    } else {
      setLoadingPreview(false);
      setPreview('');
    }

    // Auto-upload file
    await uploadFile(file);
  };

  const uploadFile = async (file) => {
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/storage/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      const result = await fetchJsonOrThrow(response, 'Failed to upload file');

      if (onFileUpload) {
        onFileUpload(result.url);
      }

      toast.success('File uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(getApiErrorMessage(error, 'Failed to upload file'));
      setSelectedFile(null);
      setPreview('');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveFile = async () => {
    if (currentFileUrl) {
      try {
        const token = getToken();
        await fetch(`${API_BASE_URL}/storage/delete`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ uri: currentFileUrl }),
        });
        
        if (onFileRemove) {
          onFileRemove();
        }
        
        toast.success('File removed successfully');
      } catch (error) {
        console.error('Delete error:', error);
        toast.error('Failed to remove file');
      }
    }
    
    setSelectedFile(null);
    setPreview('');
  };

  const clearSelection = () => {
    setSelectedFile(null);
    setPreview('');
  };

  const displayUrl = currentFileUrl || (selectedFile ? 'Uploading...' : '');

  return (
    <div className={`space-y-3 ${className}`}>
      <style>{`
        @keyframes bubble-bounce-1 {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-10px) scale(1.2); }
        }
        
        @keyframes bubble-bounce-2 {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-10px) scale(1.2); }
        }
        
        @keyframes bubble-bounce-3 {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-10px) scale(1.2); }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        @keyframes shimmer-slow {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        @keyframes spin-smooth {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes fade-in {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        
        @keyframes fade-in-out {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }
        
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 10px rgba(59, 130, 246, 0.3); }
          50% { box-shadow: 0 0 20px rgba(59, 130, 246, 0.6); }
        }
        
        .animate-bubble-1 {
          animation: bubble-bounce-1 1s ease-in-out infinite;
        }
        
        .animate-bubble-2 {
          animation: bubble-bounce-2 1s ease-in-out 0.2s infinite;
        }
        
        .animate-bubble-3 {
          animation: bubble-bounce-3 1s ease-in-out 0.4s infinite;
        }
        
        .animate-shimmer {
          animation: shimmer 1.5s infinite;
        }
        
        .animate-shimmer-slow {
          animation: shimmer-slow 2s infinite;
        }
        
        .animate-spin-smooth {
          animation: spin-smooth 1s linear infinite;
        }
        
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
        
        .animate-fade-in-out {
          animation: fade-in-out 2s ease-in-out infinite;
        }
        
        .animate-pulse-glow {
          animation: pulse-glow 2s ease-in-out infinite;
        }
      `}</style>

      {/* Show loading skeleton while processing preview */}
      {loadingPreview && <SkeletonLoader />}

      {/* Show upload animation while uploading */}
      {uploading && !loadingPreview && <LoadingAnimation type="upload" />}

      {/* Preview Section */}
      {!loadingPreview && !uploading && (preview || currentFileUrl) && (
        <div className="flex flex-col items-center space-y-2 p-4 border rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 animate-fade-in">
          {preview ? (
            <div className="relative group">
              <img
                src={preview}
                alt="File preview"
                className="w-32 h-32 object-cover rounded-lg border-2 border-blue-200 shadow-lg transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute inset-0 rounded-lg bg-blue-500 opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
            </div>
          ) : currentFileUrl ? (
            <div className="w-32 h-32 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg border-2 border-gray-300 shadow-md transition-all duration-300 hover:shadow-lg hover:scale-105">
              <DocumentIcon className="h-12 w-12 text-gray-400" />
            </div>
          ) : null}
          
          {displayUrl && (
            <p className="text-xs text-gray-600 text-center break-all max-w-xs font-medium px-2 py-1 bg-white rounded-md shadow-sm">
              {displayUrl}
            </p>
          )}
          
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={currentFileUrl ? handleRemoveFile : clearSelection}
              className="text-red-600 hover:bg-red-50 transition-all duration-200 hover:shadow-md"
              disabled={uploading}
            >
              <TrashIcon className="h-4 w-4 mr-1" />
              {currentFileUrl ? 'Delete File' : 'Remove'}
            </Button>
          </div>
        </div>
      )}

      {/* File Input */}
      {!preview && !currentFileUrl && !loadingPreview && !uploading && (
        <div className="border-2 border-dashed border-blue-300 rounded-lg p-6 text-center hover:border-blue-500 transition-all duration-300 bg-gradient-to-br from-blue-50 to-white hover:shadow-lg group">
          <div className="transition-transform duration-300 group-hover:scale-110">
            <PhotoIcon className="h-10 w-10 text-blue-500 mx-auto mb-3 transition-colors duration-300 group-hover:text-blue-600" />
          </div>
          <p className="text-sm text-gray-700 mb-3 font-medium">
            Drop your file here or click to browse
          </p>
          <Input
            type="file"
            accept={accept}
            onChange={handleFileSelect}
            className="hidden"
            id="file-upload"
            disabled={uploading}
          />
          <Label
            htmlFor="file-upload"
            className="cursor-pointer inline-block px-6 py-2.5 rounded-lg text-sm font-semibold transition-all duration-300 bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700 shadow-md hover:shadow-xl transform hover:-translate-y-0.5"
          >
            Choose File
          </Label>
          <p className="text-xs text-gray-500 mt-3">
            PNG, JPG, PDF up to {maxSize / (1024 * 1024)}MB
          </p>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
